/**
 * Button images per language (en, bg, fr, ru, tr).
 *
 * To add your own images for a button:
 * 1. Create a folder per button, e.g. playNow/, vip/, tournaments/
 * 2. Add one image per language: en.png, bg.png, fr.png, ru.png, tr.png
 * 3. Import them below and add to the corresponding entry in buttonImages.
 * Until you add language-specific files, each language falls back to the default asset.
 */

import type { ImageSourcePropType } from 'react-native';

import AdsModalWatchButtonDefault from '../adsModal/ads_modal_watch_button.png';
import AdsModalWatchButtonBg from '../adsModal/ads_modal_watch_button_bg.png';
import AdsModalWatchButtonEn from '../adsModal/ads_modal_watch_button_en.png';
import AdsModalWatchButtonFr from '../adsModal/ads_modal_watch_button_fr.png';
import AdsModalWatchButtonRu from '../adsModal/ads_modal_watch_button_ru.png';
import BtnMainPlayNowPng from '../main/playNowBtn/play.png';
import BtnMainTournamentsPng from '../main/tournamentsBtn/btn_tournaments.png';
// Default (single) assets – used as fallback for all languages until you add per-language images
import BtnMainVipPng from '../main/vipButton/btn_vip.png';
import BannerBonusGamesBg from './bonus/BANNER_BONUS_GAMES_BG.png';
// Bonus games banner – per-language images (buttonsByLanguage/bonus/)
import BannerBonusGamesEn from './bonus/BANNER_BONUS_GAMES_EN.png';
import BannerBonusGamesFr from './bonus/BANNER_BONUS_GAMES_FR.png';
import BannerBonusGamesRu from './bonus/BANNER_BONUS_GAMES_RU.png';
import BonusesBtnBg from './bottomSection/BG/BONUSES_BTN_BG.png';
import HomeBtnBg from './bottomSection/BG/BTN_HOME_BG.png';
import LobbyBtnBg from './bottomSection/BG/BTN_LOBBY_BG.png';
import BonusesHoverBg from './bottomSection/BG/games_hover_btn_BG.png';
import RankingBtnBg from './bottomSection/BG/RANKING_BTN_BG.png';
import BonusesBtnEn from './bottomSection/EN/BONUSES_BTN_EN.png';
import HomeBtnEn from './bottomSection/EN/BTN_HOME_EN.png';
import LobbyBtnEn from './bottomSection/EN/BTN_LOBBY_EN.png';
import BonusesHoverEn from './bottomSection/EN/games_hover_btn_EN.png';
// Bottom section – per-language images (buttonsByLanguage/bottomSection/{EN,BG,FR,RU,TR}/)
import RankingBtnEn from './bottomSection/EN/RANKING_BTN_EN.png';
import BonusesBtnFr from './bottomSection/FR/BONUSES_BTN_FR.png';
import HomeBtnFr from './bottomSection/FR/BTN_HOME_FR.png';
import LobbyBtnFr from './bottomSection/FR/BTN_LOBBY_FR.png';
import BonusesHoverFr from './bottomSection/FR/games_hover_btn_FR.png';
import RankingBtnFr from './bottomSection/FR/RANKING_BTN_FR.png';
import BonusesBtnRu from './bottomSection/RU/BONUSES_BTN_RU.png';
import HomeBtnRu from './bottomSection/RU/BTN_HOME_RU.png';
import LobbyBtnRu from './bottomSection/RU/BTN_LOBBY_RU.png';
import BonusesHoverRu from './bottomSection/RU/games_hover_btn_RU.png';
import RankingBtnRu from './bottomSection/RU/RANKING_BTN_RU.png';
import BonusesBtnTr from './bottomSection/TR/BONUSES_BTN_TR.png';
import HomeBtnTr from './bottomSection/TR/BTN_HOME_TR.png';
import LobbyBtnTr from './bottomSection/TR/BTN_LOBBY_TR.png';
import BonusesHoverTr from './bottomSection/TR/games_hover_btn_TR.png';
import RankingBtnTr from './bottomSection/TR/RANKING_BTN_TURKISH.png';

export const SUPPORTED_LANG_CODES = ['en', 'bg', 'fr', 'ru', 'tr'] as const;
export type LangCode = (typeof SUPPORTED_LANG_CODES)[number];

export type MainButtonKey = 'vip' | 'playNow' | 'tournaments';

export type BottomSectionButtonKey = 'ranking' | 'lobby' | 'home' | 'bonuses' | 'bonusesHover';

export type BonusBannerKey = 'bonusBanner';

const buttonImages: Record<MainButtonKey, Record<LangCode, ImageSourcePropType>> = {
	vip: {
		en: BtnMainVipPng,
		bg: BtnMainVipPng,
		fr: BtnMainVipPng,
		ru: BtnMainVipPng,
		tr: BtnMainVipPng,
	},
	playNow: {
		en: BtnMainPlayNowPng,
		bg: BtnMainPlayNowPng,
		fr: BtnMainPlayNowPng,
		ru: BtnMainPlayNowPng,
		tr: BtnMainPlayNowPng,
	},
	tournaments: {
		en: BtnMainTournamentsPng,
		bg: BtnMainTournamentsPng,
		fr: BtnMainTournamentsPng,
		ru: BtnMainTournamentsPng,
		tr: BtnMainTournamentsPng,
	},
};

const bottomSectionButtonImages: Record<
	BottomSectionButtonKey,
	Record<LangCode, ImageSourcePropType>
> = {
	ranking: {
		en: RankingBtnEn,
		bg: RankingBtnBg,
		fr: RankingBtnFr,
		ru: RankingBtnRu,
		tr: RankingBtnTr,
	},
	home: {
		en: HomeBtnEn,
		bg: HomeBtnBg,
		fr: HomeBtnFr,
		ru: HomeBtnRu,
		tr: HomeBtnTr,
	},
	lobby: {
		en: LobbyBtnEn,
		bg: LobbyBtnBg,
		fr: LobbyBtnFr,
		ru: LobbyBtnRu,
		tr: LobbyBtnTr,
	},
	bonuses: {
		en: BonusesBtnEn,
		bg: BonusesBtnBg,
		fr: BonusesBtnFr,
		ru: BonusesBtnRu,
		tr: BonusesBtnTr,
	},
	bonusesHover: {
		en: BonusesHoverEn,
		bg: BonusesHoverBg,
		fr: BonusesHoverFr,
		ru: BonusesHoverRu,
		tr: BonusesHoverTr,
	},
};

const bonusBannerImages: Record<LangCode, ImageSourcePropType> = {
	en: BannerBonusGamesEn,
	bg: BannerBonusGamesBg,
	fr: BannerBonusGamesFr,
	ru: BannerBonusGamesRu,
	// TR not yet available – fall back to EN
	tr: BannerBonusGamesEn,
};

const adModalButtonImages: Record<LangCode, ImageSourcePropType> = {
	en: AdsModalWatchButtonEn,
	bg: AdsModalWatchButtonBg,
	fr: AdsModalWatchButtonFr,
	ru: AdsModalWatchButtonRu,
	tr: AdsModalWatchButtonDefault,
};

export const getButtonImageByLanguage = (
	buttonKey: MainButtonKey,
	langCode: string,
): ImageSourcePropType => {
	const lang = SUPPORTED_LANG_CODES.includes(langCode as LangCode) ? (langCode as LangCode) : 'en';
	return buttonImages[buttonKey][lang];
};

export const getBottomSectionButtonImageByLanguage = (
	buttonKey: BottomSectionButtonKey,
	langCode: string,
): ImageSourcePropType => {
	const lang = SUPPORTED_LANG_CODES.includes(langCode as LangCode) ? (langCode as LangCode) : 'en';
	return bottomSectionButtonImages[buttonKey][lang];
};

export const getBonusBannerImageByLanguage = (langCode: string): ImageSourcePropType => {
	const lang = SUPPORTED_LANG_CODES.includes(langCode as LangCode) ? (langCode as LangCode) : 'en';

	return bonusBannerImages[lang] ?? BannerBonusGamesEn;
};

export const getAdModalButtonImageByLanguage = (langCode: string): ImageSourcePropType => {
	const lang = SUPPORTED_LANG_CODES.includes(langCode as LangCode) ? (langCode as LangCode) : 'en';

	return adModalButtonImages[lang] ?? AdsModalWatchButtonDefault;
};
